type StoredTtmlFile = {
  name: string;
  content: string;
  filename?: string;
  generatedAt?: Date | string;
  checksum?: string;
  validationStats?: Record<string, unknown>;
  validationErrors?: unknown[];
  validationWarnings?: unknown[];
  isValid?: boolean;
};

type StoredSessionData = {
  sessionId: string;
  transcriptionChecksum: string;
  filename: string;
  title?: string;
  thumbnailUrl?: string | null;
  isYouTubeMode: boolean;
  youtubeUrl?: string;
  transcriptionData: any;
  speakerLabels: any;
  finalTranscript: string;
  currentTranscript: string;
  speakerSegments: any;
  distinctSpeakers: number[];
  generatedTtmlFiles: StoredTtmlFile[];
  selectedTtmlId?: string | null;
  youtubeDescription?: string;
  _comments?: any[];
  createdAt?: Date | string;
  updatedAt?: Date | string;
  duration?: number;
};

export interface OfflineSessionSummary {
  sessionId: string;
  shareToken?: string | null;
  thumbnailUrl?: string | null;
  title: string;
  filename: string;
  transcriptPreview: string;
  descriptionPreview: string;
  duration: number;
  wordCount: number;
  savedAt: string;
  updatedAt: string;
}

export interface OfflineSessionRecord extends OfflineSessionSummary {
  sessionData: StoredSessionData;
  audioMimeType: string;
  audioBlob?: Blob;
}

interface OfflineSessionPayloadRecord {
  sessionId: string;
  sessionData: StoredSessionData;
}

interface OfflineSessionAudioRecord {
  sessionId: string;
  audioBlob?: Blob;
  audioMimeType: string;
  storageKind?: 'blob' | 'chunked';
  chunkCount?: number;
}

interface OfflineSessionAudioChunkRecord {
  id: string;
  sessionId: string;
  index: number;
  chunk: Uint8Array;
}

interface OfflineShareTokenRecord {
  shareToken: string;
  sessionId: string;
  savedAt: string;
}

type OfflineSessionSummaryMap = Record<string, OfflineSessionSummary>;
type OfflineShareMap = Record<string, string>;
type OfflineSaveProgressCallback = (message: string, details?: Record<string, unknown>) => void;

const DB_NAME = 'mlst-offline-sessions';
const DB_VERSION = 3;
const SUMMARY_STORE = 'summaries';
const PAYLOAD_STORE = 'payloads';
const AUDIO_STORE = 'audio';
const AUDIO_CHUNK_STORE = 'audioChunks';
const SHARE_TOKEN_STORE = 'shareTokens';
const INDEXED_DB_AUDIO_CHUNK_BYTES = 1024 * 1024;

const CACHE_NAME = 'mlst-offline-session-assets-v1';
const LOCAL_SUMMARIES_KEY = 'mlst-offline-summaries-v1';
const LOCAL_SHARE_MAP_KEY = 'mlst-offline-share-map-v1';
const PAYLOAD_CACHE_PREFIX = '/__offline_cache__/sessions';

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
  });
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error(message));
    }, timeoutMs);

    promise
      .then((value) => {
        window.clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        window.clearTimeout(timer);
        reject(error);
      });
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error('IndexedDB transaction failed'));
    transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted'));
  });
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is unavailable in this browser context'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const database = request.result;

      if (!database.objectStoreNames.contains(SUMMARY_STORE)) {
        database.createObjectStore(SUMMARY_STORE, { keyPath: 'sessionId' });
      }

      if (!database.objectStoreNames.contains(PAYLOAD_STORE)) {
        database.createObjectStore(PAYLOAD_STORE, { keyPath: 'sessionId' });
      }

      if (!database.objectStoreNames.contains(AUDIO_STORE)) {
        database.createObjectStore(AUDIO_STORE, { keyPath: 'sessionId' });
      }

      if (!database.objectStoreNames.contains(AUDIO_CHUNK_STORE)) {
        const audioChunkStore = database.createObjectStore(AUDIO_CHUNK_STORE, { keyPath: 'id' });
        audioChunkStore.createIndex('sessionId', 'sessionId', { unique: false });
      }

      if (!database.objectStoreNames.contains(SHARE_TOKEN_STORE)) {
        const shareTokenStore = database.createObjectStore(SHARE_TOKEN_STORE, { keyPath: 'shareToken' });
        shareTokenStore.createIndex('sessionId', 'sessionId', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Failed to open IndexedDB'));
  });
}

function buildPreview(value: string | undefined, maxLength = 180): string {
  const normalized = (value || '').replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return '';
  }

  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, maxLength).trimEnd()}...`;
}

function buildSummary(sessionData: StoredSessionData, savedAt: string, shareToken?: string | null): OfflineSessionSummary {
  const finalTranscript = sessionData.finalTranscript || sessionData.currentTranscript || '';
  const transcriptWordCount = finalTranscript.trim()
    ? finalTranscript.trim().split(/\s+/).length
    : 0;

  return {
    sessionId: sessionData.sessionId,
    shareToken: shareToken || null,
    thumbnailUrl: sessionData.thumbnailUrl || null,
    title: sessionData.title || sessionData.filename || 'Untitled Session',
    filename: sessionData.filename || 'Untitled Session',
    transcriptPreview: buildPreview(finalTranscript, 220),
    descriptionPreview: buildPreview(sessionData.youtubeDescription, 180),
    duration: Number(sessionData.duration) || 0,
    wordCount: transcriptWordCount,
    savedAt,
    updatedAt: savedAt,
  };
}

function getPayloadCacheUrl(sessionId: string): string {
  return `${PAYLOAD_CACHE_PREFIX}/${encodeURIComponent(sessionId)}/payload.json`;
}

function getAudioCacheUrl(sessionId: string): string {
  return `${PAYLOAD_CACHE_PREFIX}/${encodeURIComponent(sessionId)}/audio`;
}

function getThumbnailCacheUrl(sessionId: string): string {
  return `${PAYLOAD_CACHE_PREFIX}/${encodeURIComponent(sessionId)}/thumbnail`;
}

export function getOfflineThumbnailCacheUrl(sessionId: string): string {
  return getThumbnailCacheUrl(sessionId);
}

function readLocalStorageJson<T>(key: string, fallback: T): T {
  if (typeof localStorage === 'undefined') {
    return fallback;
  }

  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return fallback;
    }
    return JSON.parse(raw) as T;
  } catch (error) {
    console.warn('[OFFLINE] Failed to read local storage key:', key, error);
    return fallback;
  }
}

function writeLocalStorageJson<T>(key: string, value: T): void {
  if (typeof localStorage === 'undefined') {
    return;
  }

  localStorage.setItem(key, JSON.stringify(value));
}

function readSummaryMap(): OfflineSessionSummaryMap {
  return readLocalStorageJson<OfflineSessionSummaryMap>(LOCAL_SUMMARIES_KEY, {});
}

function writeSummaryMap(summaryMap: OfflineSessionSummaryMap): void {
  writeLocalStorageJson(LOCAL_SUMMARIES_KEY, summaryMap);
}

function readShareMap(): OfflineShareMap {
  return readLocalStorageJson<OfflineShareMap>(LOCAL_SHARE_MAP_KEY, {});
}

function writeShareMap(shareMap: OfflineShareMap): void {
  writeLocalStorageJson(LOCAL_SHARE_MAP_KEY, shareMap);
}

function writeLocalOfflineMetadata(summary: OfflineSessionSummary, shareToken?: string | null): void {
  const summaryMap = readSummaryMap();
  summaryMap[summary.sessionId] = summary;
  writeSummaryMap(summaryMap);

  if (shareToken) {
    const shareMap = readShareMap();
    shareMap[shareToken] = summary.sessionId;
    writeShareMap(shareMap);
  }
}

async function openOfflineCache(): Promise<Cache> {
  if (typeof caches === 'undefined') {
    throw new Error('Cache Storage is unavailable in this browser context');
  }

  return caches.open(CACHE_NAME);
}

async function cacheSessionResources(
  sessionData: StoredSessionData,
  audioSource: Response | Blob,
  audioMimeType: string,
  thumbnailSource: Response | null,
  onProgress?: OfflineSaveProgressCallback
): Promise<void> {
  onProgress?.('Opening browser cache', { sessionId: sessionData.sessionId });
  const cache = await openOfflineCache();
  const payloadUrl = getPayloadCacheUrl(sessionData.sessionId);
  const audioUrl = getAudioCacheUrl(sessionData.sessionId);
  const thumbnailUrl = getThumbnailCacheUrl(sessionData.sessionId);
  const audioResponse = audioSource instanceof Response
    ? audioSource
    : new Response(audioSource, {
        headers: { 'Content-Type': audioMimeType || 'audio/mpeg' },
      });

  onProgress?.('Saving transcript and metadata', { payloadUrl });

  await Promise.all([
    cache.put(
      payloadUrl,
      new Response(JSON.stringify({ sessionData }), {
        headers: { 'Content-Type': 'application/json' },
      })
    ),
    (async () => {
      onProgress?.('Saving audio to browser storage', {
        audioUrl,
        audioMimeType,
      });
      await cache.put(audioUrl, audioResponse);
    })(),
    thumbnailSource
      ? (async () => {
          onProgress?.('Saving thumbnail for offline cards', {
            thumbnailUrl,
          });
          await cache.put(thumbnailUrl, thumbnailSource);
        })()
      : Promise.resolve(),
  ]);

  onProgress?.('Browser cache write completed', { sessionId: sessionData.sessionId });
}

type CacheResourceInspection = {
  hasPayload: boolean;
  hasAudio: boolean;
};

async function inspectCachedSessionResources(sessionId: string): Promise<CacheResourceInspection> {
  const cache = await openOfflineCache();
  const [payloadResponse, audioResponse] = await Promise.all([
    cache.match(getPayloadCacheUrl(sessionId)),
    cache.match(getAudioCacheUrl(sessionId)),
  ]);

  return {
    hasPayload: !!payloadResponse,
    hasAudio: !!audioResponse,
  };
}

async function hasCachedSessionResources(sessionId: string): Promise<boolean> {
  try {
    const inspection = await inspectCachedSessionResources(sessionId);
    return inspection.hasPayload && inspection.hasAudio;
  } catch (error) {
    console.warn('[OFFLINE] Failed to inspect Cache Storage resources:', error);
    return false;
  }
}

async function verifyCachedSessionResources(
  sessionId: string,
  onProgress?: OfflineSaveProgressCallback
): Promise<boolean> {
  const retryDelaysMs = [0, 400, 1200, 2500, 5000];

  for (let attemptIndex = 0; attemptIndex < retryDelaysMs.length; attemptIndex += 1) {
    const delayMs = retryDelaysMs[attemptIndex];
    if (delayMs > 0) {
      onProgress?.('Waiting for browser cache to settle', {
        sessionId,
        attempt: attemptIndex + 1,
        delayMs,
      });
      await new Promise((resolve) => window.setTimeout(resolve, delayMs));
    }

    try {
      const inspection = await inspectCachedSessionResources(sessionId);
      if (inspection.hasPayload && inspection.hasAudio) {
        onProgress?.('Verified saved audio and transcript', {
          sessionId,
          attempt: attemptIndex + 1,
        });
        return true;
      }

      onProgress?.('Browser cache verification is still incomplete', {
        sessionId,
        attempt: attemptIndex + 1,
        ...inspection,
      });
    } catch (error) {
      onProgress?.('Browser cache verification failed', {
        sessionId,
        attempt: attemptIndex + 1,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  return false;
}

async function getCachedSessionRecord(
  sessionId: string,
  summary: OfflineSessionSummary
): Promise<OfflineSessionRecord | null> {
  try {
    const cache = await openOfflineCache();
    const [payloadResponse, audioResponse] = await Promise.all([
      cache.match(getPayloadCacheUrl(sessionId)),
      cache.match(getAudioCacheUrl(sessionId)),
    ]);

    if (!payloadResponse || !audioResponse) {
      return null;
    }

    const payload = await payloadResponse.json() as { sessionData?: StoredSessionData };
    if (!payload.sessionData) {
      return null;
    }

    const audioBlob = await audioResponse.blob();

    return {
      ...summary,
      sessionData: payload.sessionData,
      audioBlob,
      audioMimeType: audioBlob.type || audioResponse.headers.get('Content-Type') || 'audio/mpeg',
    };
  } catch (error) {
    console.warn('[OFFLINE] Failed to load cached session record:', error);
    return null;
  }
}

async function deleteCachedSessionResources(sessionId: string): Promise<void> {
  try {
    const cache = await openOfflineCache();
    await Promise.all([
      cache.delete(getPayloadCacheUrl(sessionId)),
      cache.delete(getAudioCacheUrl(sessionId)),
      cache.delete(getThumbnailCacheUrl(sessionId)),
    ]);
  } catch (error) {
    console.warn('[OFFLINE] Failed to delete cached session resources:', error);
  }
}

async function saveIndexedDbOfflineSession(
  sessionData: StoredSessionData,
  audioBlob: Blob | null,
  audioMimeType: string | null,
  options?: { shareToken?: string | null }
): Promise<void> {
  const database = await openDatabase();
  const savedAt = new Date().toISOString();
  const summary = buildSummary(sessionData, savedAt, options?.shareToken);

  try {
    const transaction = database.transaction([SUMMARY_STORE, PAYLOAD_STORE, AUDIO_STORE, SHARE_TOKEN_STORE], 'readwrite');
    transaction.objectStore(SUMMARY_STORE).put(summary);
    transaction.objectStore(PAYLOAD_STORE).put({
      sessionId: sessionData.sessionId,
      sessionData,
    } satisfies OfflineSessionPayloadRecord);

    if (audioBlob) {
      transaction.objectStore(AUDIO_STORE).put({
        sessionId: sessionData.sessionId,
        audioBlob,
        audioMimeType: audioMimeType || 'audio/mpeg',
        storageKind: 'blob',
      } satisfies OfflineSessionAudioRecord);
    }

    if (options?.shareToken) {
      transaction.objectStore(SHARE_TOKEN_STORE).put({
        shareToken: options.shareToken,
        sessionId: sessionData.sessionId,
        savedAt,
      } satisfies OfflineShareTokenRecord);
    }

    await transactionDone(transaction);
  } finally {
    database.close();
  }
}

async function deleteIndexedDbAudioChunks(database: IDBDatabase, sessionId: string): Promise<void> {
  const transaction = database.transaction(AUDIO_CHUNK_STORE, 'readwrite');
  const chunkStore = transaction.objectStore(AUDIO_CHUNK_STORE);
  const sessionIndex = chunkStore.index('sessionId');
  const chunkIds = (await requestToPromise(sessionIndex.getAllKeys(sessionId))) as IDBValidKey[];
  chunkIds.forEach((chunkId) => {
    chunkStore.delete(chunkId);
  });
  await transactionDone(transaction);
}

async function saveIndexedDbChunkedAudio(
  sessionId: string,
  audioResponse: Response,
  audioMimeType: string,
  onProgress?: OfflineSaveProgressCallback
): Promise<void> {
  const database = await openDatabase();

  try {
    await deleteIndexedDbAudioChunks(database, sessionId);

    const reader = audioResponse.body?.getReader();
    if (!reader) {
      const audioBlob = await withTimeout(
        audioResponse.blob(),
        45000,
        'Preparing the offline audio fallback took too long on this device.'
      );
      const transaction = database.transaction(AUDIO_STORE, 'readwrite');
      transaction.objectStore(AUDIO_STORE).put({
        sessionId,
        audioBlob,
        audioMimeType,
        storageKind: 'blob',
      } satisfies OfflineSessionAudioRecord);
      await transactionDone(transaction);
      return;
    }

    let chunkCount = 0;
    let totalBytes = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      if (!value || value.byteLength === 0) {
        continue;
      }

      for (let offset = 0; offset < value.byteLength; offset += INDEXED_DB_AUDIO_CHUNK_BYTES) {
        const chunk = value.slice(offset, offset + INDEXED_DB_AUDIO_CHUNK_BYTES);
        const transaction = database.transaction(AUDIO_CHUNK_STORE, 'readwrite');
        transaction.objectStore(AUDIO_CHUNK_STORE).put({
          id: `${sessionId}:${chunkCount}`,
          sessionId,
          index: chunkCount,
          chunk,
        } satisfies OfflineSessionAudioChunkRecord);
        await transactionDone(transaction);

        chunkCount += 1;
        totalBytes += chunk.byteLength;

        if (chunkCount === 1 || chunkCount % 4 === 0) {
          onProgress?.('Saving audio in fallback browser storage', {
            sessionId,
            audioMimeType,
            chunkCount,
            totalBytes,
          });
        }
      }
    }

    if (chunkCount === 0) {
      throw new Error('The offline audio fallback did not produce any audio chunks.');
    }

    const transaction = database.transaction(AUDIO_STORE, 'readwrite');
    transaction.objectStore(AUDIO_STORE).put({
      sessionId,
      audioMimeType,
      storageKind: 'chunked',
      chunkCount,
    } satisfies OfflineSessionAudioRecord);
    await transactionDone(transaction);
  } finally {
    database.close();
  }
}

async function listIndexedDbOfflineSessions(): Promise<OfflineSessionSummary[]> {
  const database = await openDatabase();

  try {
    const transaction = database.transaction(SUMMARY_STORE, 'readonly');
    const summaries = (await requestToPromise(transaction.objectStore(SUMMARY_STORE).getAll())) as OfflineSessionSummary[];
    await transactionDone(transaction);
    return summaries;
  } finally {
    database.close();
  }
}

async function getIndexedDbOfflineSummary(sessionId: string): Promise<OfflineSessionSummary | null> {
  const database = await openDatabase();

  try {
    const transaction = database.transaction(SUMMARY_STORE, 'readonly');
    const summary = await requestToPromise(transaction.objectStore(SUMMARY_STORE).get(sessionId));
    await transactionDone(transaction);
    return (summary as OfflineSessionSummary | undefined) || null;
  } finally {
    database.close();
  }
}

async function getIndexedDbOfflineSession(sessionId: string): Promise<OfflineSessionRecord | null> {
  const database = await openDatabase();

  try {
    const transaction = database.transaction([SUMMARY_STORE, PAYLOAD_STORE, AUDIO_STORE], 'readonly');
    const [summary, payload, audio] = await Promise.all([
      requestToPromise(transaction.objectStore(SUMMARY_STORE).get(sessionId)),
      requestToPromise(transaction.objectStore(PAYLOAD_STORE).get(sessionId)),
      requestToPromise(transaction.objectStore(AUDIO_STORE).get(sessionId)),
    ]);
    await transactionDone(transaction);

    if (!summary || !payload || !audio) {
      return null;
    }

    const payloadRecord = payload as OfflineSessionPayloadRecord;
    const audioRecord = audio as OfflineSessionAudioRecord;
    let audioBlob = audioRecord.audioBlob;

    if (!audioBlob && audioRecord.storageKind === 'chunked') {
      const chunkTransaction = database.transaction(AUDIO_CHUNK_STORE, 'readonly');
      const chunkStore = chunkTransaction.objectStore(AUDIO_CHUNK_STORE);
      const sessionIndex = chunkStore.index('sessionId');
      const chunkRecords = (await requestToPromise(sessionIndex.getAll(sessionId))) as OfflineSessionAudioChunkRecord[];
      await transactionDone(chunkTransaction);

      if (!chunkRecords.length) {
        return null;
      }

      chunkRecords.sort((left, right) => left.index - right.index);
      audioBlob = new Blob(
        chunkRecords.map((chunkRecord) => chunkRecord.chunk),
        { type: audioRecord.audioMimeType || 'audio/mpeg' }
      );
    }

    if (!audioBlob) {
      return null;
    }

    return {
      ...(summary as OfflineSessionSummary),
      sessionData: payloadRecord.sessionData,
      audioBlob,
      audioMimeType: audioRecord.audioMimeType,
    };
  } finally {
    database.close();
  }
}

async function hasIndexedDbOfflineRecord(sessionId: string): Promise<boolean> {
  const database = await openDatabase();

  try {
    const transaction = database.transaction([PAYLOAD_STORE, AUDIO_STORE], 'readonly');
    const [payload, audio] = await Promise.all([
      requestToPromise(transaction.objectStore(PAYLOAD_STORE).get(sessionId)),
      requestToPromise(transaction.objectStore(AUDIO_STORE).get(sessionId)),
    ]);
    await transactionDone(transaction);

    if (!payload || !audio) {
      return false;
    }

    const audioRecord = audio as OfflineSessionAudioRecord;
    if (audioRecord.audioBlob) {
      return true;
    }

    return audioRecord.storageKind === 'chunked' && Number(audioRecord.chunkCount) > 0;
  } finally {
    database.close();
  }
}

async function removeIndexedDbOfflineSession(sessionId: string): Promise<void> {
  const database = await openDatabase();

  try {
    const transaction = database.transaction([SUMMARY_STORE, PAYLOAD_STORE, AUDIO_STORE, SHARE_TOKEN_STORE, AUDIO_CHUNK_STORE], 'readwrite');
    transaction.objectStore(SUMMARY_STORE).delete(sessionId);
    transaction.objectStore(PAYLOAD_STORE).delete(sessionId);
    transaction.objectStore(AUDIO_STORE).delete(sessionId);

    const audioChunkStore = transaction.objectStore(AUDIO_CHUNK_STORE);
    const audioChunkIndex = audioChunkStore.index('sessionId');
    const audioChunkIds = (await requestToPromise(audioChunkIndex.getAllKeys(sessionId))) as IDBValidKey[];
    audioChunkIds.forEach((chunkId) => {
      audioChunkStore.delete(chunkId);
    });

    const shareTokenStore = transaction.objectStore(SHARE_TOKEN_STORE);
    const sessionIdIndex = shareTokenStore.index('sessionId');
    const shareTokens = (await requestToPromise(sessionIdIndex.getAllKeys(sessionId))) as IDBValidKey[];
    shareTokens.forEach((shareToken) => {
      shareTokenStore.delete(shareToken);
    });

    await transactionDone(transaction);
  } finally {
    database.close();
  }
}

async function getIndexedDbSessionIdForShare(shareToken: string): Promise<string | null> {
  const database = await openDatabase();

  try {
    const transaction = database.transaction(SHARE_TOKEN_STORE, 'readonly');
    const shareRecord = await requestToPromise(transaction.objectStore(SHARE_TOKEN_STORE).get(shareToken));
    await transactionDone(transaction);
    return (shareRecord as OfflineShareTokenRecord | undefined)?.sessionId || null;
  } finally {
    database.close();
  }
}

async function linkIndexedDbShareTokenToSession(shareToken: string, sessionId: string): Promise<void> {
  const database = await openDatabase();

  try {
    const transaction = database.transaction(SHARE_TOKEN_STORE, 'readwrite');
    transaction.objectStore(SHARE_TOKEN_STORE).put({
      shareToken,
      sessionId,
      savedAt: new Date().toISOString(),
    } satisfies OfflineShareTokenRecord);
    await transactionDone(transaction);
  } finally {
    database.close();
  }
}

function sortOfflineSessions(summaries: OfflineSessionSummary[]): OfflineSessionSummary[] {
  return summaries.sort((left, right) => {
    return new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();
  });
}

function dedupeOfflineSummaries(summaries: OfflineSessionSummary[]): OfflineSessionSummary[] {
  const summaryMap = new Map<string, OfflineSessionSummary>();

  summaries.forEach((summary) => {
    const existing = summaryMap.get(summary.sessionId);
    if (!existing || new Date(summary.updatedAt).getTime() > new Date(existing.updatedAt).getTime()) {
      summaryMap.set(summary.sessionId, summary);
    }
  });

  return Array.from(summaryMap.values());
}

export async function saveOfflineSession(
  sessionData: StoredSessionData,
  audioResponse: Response,
  options?: { shareToken?: string | null; onProgress?: OfflineSaveProgressCallback; thumbnailUrl?: string | null }
): Promise<OfflineSessionSummary> {
  const savedAt = new Date().toISOString();
  const summary = buildSummary(sessionData, savedAt, options?.shareToken);
  let hasPlayableOfflineCopy = false;
  let savedIndexedDbMetadata = false;
  let primaryStorageError: Error | null = null;
  const audioMimeType = audioResponse.headers.get('Content-Type') || 'audio/mpeg';
  const fallbackAudioResponse = audioResponse.clone();
  let thumbnailResponse: Response | null = null;
  let cacheVerified = false;

  if (options?.thumbnailUrl) {
    try {
      thumbnailResponse = await fetch(options.thumbnailUrl, { mode: 'no-cors' });
    } catch (error) {
      console.warn('[OFFLINE] Failed to cache thumbnail for offline card:', error);
    }
  }

  try {
    await cacheSessionResources(sessionData, audioResponse, audioMimeType, thumbnailResponse, options?.onProgress);
    options?.onProgress?.('Verifying saved audio and transcript', { sessionId: sessionData.sessionId });
    cacheVerified = await verifyCachedSessionResources(sessionData.sessionId, options?.onProgress);
    if (!cacheVerified) {
      throw new Error('The browser did not retain the offline audio in Cache Storage.');
    }

    hasPlayableOfflineCopy = true;
  } catch (error) {
    primaryStorageError = error instanceof Error ? error : new Error('Failed to cache offline session assets');
    console.warn('[OFFLINE] Primary cache storage failed, attempting IndexedDB fallback:', error);
    if (cacheVerified === false) {
      await deleteCachedSessionResources(sessionData.sessionId);
    }
  }

  if (hasPlayableOfflineCopy) {
    try {
      options?.onProgress?.('Recording offline library entry', { sessionId: summary.sessionId });
      writeLocalOfflineMetadata(summary, options?.shareToken);
    } catch (error) {
      console.warn('[OFFLINE] Failed to write local offline metadata:', error);
    }
  }

  try {
    options?.onProgress?.('Recording browser fallback metadata', { sessionId: summary.sessionId });
    await saveIndexedDbOfflineSession(sessionData, null, audioMimeType, options);
    savedIndexedDbMetadata = true;
  } catch (error) {
    console.warn('[OFFLINE] IndexedDB metadata backup save failed:', error);
  }

  if (!hasPlayableOfflineCopy) {
    try {
      options?.onProgress?.('Primary cache failed, trying fallback storage', { sessionId: sessionData.sessionId });
      options?.onProgress?.('Saving audio in fallback browser storage', {
        sessionId: sessionData.sessionId,
        audioMimeType,
      });
      await withTimeout(
        saveIndexedDbChunkedAudio(sessionData.sessionId, fallbackAudioResponse, audioMimeType, options?.onProgress),
        180000,
        'Saving audio in fallback browser storage took too long on this device.'
      );
      hasPlayableOfflineCopy = true;
      savedIndexedDbMetadata = true;
    } catch (error) {
      console.warn('[OFFLINE] IndexedDB audio fallback save failed:', error);
    }
  }

  if (!hasPlayableOfflineCopy) {
    if (savedIndexedDbMetadata) {
      try {
        await removeIndexedDbOfflineSession(sessionData.sessionId);
      } catch (cleanupError) {
        console.warn('[OFFLINE] Failed to clean up incomplete IndexedDB offline session:', cleanupError);
      }
    }
    throw primaryStorageError || new Error('Failed to store offline session locally');
  }

  return summary;
}

export async function listOfflineSessions(): Promise<OfflineSessionSummary[]> {
  const localSummaries = Object.values(readSummaryMap());
  const validLocalSummaries = await Promise.all(localSummaries.map(async (summary) => {
    const hasCachedResources = await hasCachedSessionResources(summary.sessionId);
    return hasCachedResources ? summary : null;
  }));

  const filteredLocalSummaries = validLocalSummaries.filter((summary): summary is OfflineSessionSummary => summary !== null);

  if (filteredLocalSummaries.length !== localSummaries.length) {
    const rebuiltSummaryMap = filteredLocalSummaries.reduce<OfflineSessionSummaryMap>((accumulator, summary) => {
      accumulator[summary.sessionId] = summary;
      return accumulator;
    }, {});
    writeSummaryMap(rebuiltSummaryMap);

    const validSessionIds = new Set(filteredLocalSummaries.map((summary) => summary.sessionId));
    const shareMap = readShareMap();
    const filteredShareMap = Object.fromEntries(
      Object.entries(shareMap).filter(([, sessionId]) => validSessionIds.has(sessionId))
    );
    writeShareMap(filteredShareMap);
  }

  let indexedDbSummaries: OfflineSessionSummary[] = [];
  try {
    const rawIndexedDbSummaries = await listIndexedDbOfflineSessions();
    const validatedIndexedDbSummaries = await Promise.all(rawIndexedDbSummaries.map(async (summary) => {
      const [hasCachedResources, hasIndexedDbRecord] = await Promise.all([
        hasCachedSessionResources(summary.sessionId),
        hasIndexedDbOfflineRecord(summary.sessionId),
      ]);

      return hasCachedResources || hasIndexedDbRecord ? summary : null;
    }));

    indexedDbSummaries = validatedIndexedDbSummaries.filter((summary): summary is OfflineSessionSummary => summary !== null);
  } catch (error) {
    console.warn('[OFFLINE] Failed to list IndexedDB offline sessions:', error);
  }

  return sortOfflineSessions(dedupeOfflineSummaries([...filteredLocalSummaries, ...indexedDbSummaries]));
}

export async function getOfflineSession(sessionId: string): Promise<OfflineSessionRecord | null> {
  const localSummary = readSummaryMap()[sessionId];
  let summaryForCachedLookup: OfflineSessionSummary | null = localSummary || null;

  if (!summaryForCachedLookup) {
    try {
      summaryForCachedLookup = await getIndexedDbOfflineSummary(sessionId);
    } catch (error) {
      console.warn('[OFFLINE] Failed to load IndexedDB summary for cache lookup:', error);
    }
  }

  if (summaryForCachedLookup) {
    const cachedRecord = await getCachedSessionRecord(sessionId, summaryForCachedLookup);
    if (cachedRecord) {
      return cachedRecord;
    }
  }

  try {
    return await getIndexedDbOfflineSession(sessionId);
  } catch (error) {
    console.warn('[OFFLINE] Failed to load IndexedDB offline session:', error);
    return null;
  }
}

export async function removeOfflineSession(sessionId: string): Promise<void> {
  const summaryMap = readSummaryMap();
  if (summaryMap[sessionId]) {
    delete summaryMap[sessionId];
    writeSummaryMap(summaryMap);
  }

  const shareMap = readShareMap();
  Object.entries(shareMap).forEach(([shareToken, mappedSessionId]) => {
    if (mappedSessionId === sessionId) {
      delete shareMap[shareToken];
    }
  });
  writeShareMap(shareMap);

  await deleteCachedSessionResources(sessionId);

  try {
    await removeIndexedDbOfflineSession(sessionId);
  } catch (error) {
    console.warn('[OFFLINE] Failed to remove IndexedDB offline session:', error);
  }
}

export async function isSessionAvailableOffline(sessionId: string): Promise<boolean> {
  const localSummary = readSummaryMap()[sessionId];
  if (localSummary) {
    const [hasCachedResources, hasIndexedDbRecord] = await Promise.all([
      hasCachedSessionResources(sessionId),
      hasIndexedDbOfflineRecord(sessionId).catch((error) => {
        console.warn('[OFFLINE] Failed to check IndexedDB offline record:', error);
        return false;
      }),
    ]);
    return hasCachedResources || hasIndexedDbRecord;
  }

  try {
    const indexedDbSummary = await getIndexedDbOfflineSummary(sessionId);
    if (indexedDbSummary) {
      const [hasCachedResources, hasIndexedDbRecord] = await Promise.all([
        hasCachedSessionResources(sessionId),
        hasIndexedDbOfflineRecord(sessionId),
      ]);
      return hasCachedResources || hasIndexedDbRecord;
    }
  } catch (error) {
    console.warn('[OFFLINE] Failed to check IndexedDB summary availability:', error);
  }

  try {
    return !!(await getIndexedDbOfflineSession(sessionId));
  } catch (error) {
    console.warn('[OFFLINE] Failed to check IndexedDB offline availability:', error);
    return false;
  }
}

export async function getOfflineSessionIdForShare(shareToken: string): Promise<string | null> {
  const localShareMap = readShareMap();
  const localSessionId = localShareMap[shareToken];
  if (localSessionId) {
    const isAvailable = await isSessionAvailableOffline(localSessionId);
    if (isAvailable) {
      return localSessionId;
    }
  }

  try {
    const indexedDbSessionId = await getIndexedDbSessionIdForShare(shareToken);
    if (!indexedDbSessionId) {
      return null;
    }

    const isAvailable = await isSessionAvailableOffline(indexedDbSessionId);
    return isAvailable ? indexedDbSessionId : null;
  } catch (error) {
    console.warn('[OFFLINE] Failed to read IndexedDB share token mapping:', error);
    return null;
  }
}

export async function linkOfflineShareTokenToSession(shareToken: string, sessionId: string): Promise<void> {
  const shareMap = readShareMap();
  shareMap[shareToken] = sessionId;
  writeShareMap(shareMap);

  try {
    await linkIndexedDbShareTokenToSession(shareToken, sessionId);
  } catch (error) {
    console.warn('[OFFLINE] Failed to update IndexedDB share token mapping:', error);
  }
}
